/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import React from "react";
import clsx from "clsx";

import { ITeacher, IClass, ISchoolManager, IFile } from "../../../state";
import { MAX_IMAGE_SIZE_MB } from "../../../constants/upload";
import { emailRegex } from "../../../constants/regex";
import { ModalContext } from "../../../libs/ModalProvider";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import Avatar from "../../../molecules/Avatar";
import Confirm from "../../../molecules/Confirm";
import RadioGroup from "../../../molecules/RadioGroup";

import styles from "./TeacherForm.module.scss";

export interface TeacherFormProps {
  title: string;
  calling: boolean;
  error: string;
  school: ISchoolManager;
  teacher?: ITeacher;
  classes?: IClass[];
  isMyself?: boolean;
  upsertTeacher: (params: {
    id: number;
    first_name: string;
    last_name: string;
    first_name_kana: string;
    last_name_kana: string;
    email: string;
    serviced: boolean;
    use_as_g4s_student: boolean;
    entrance_year?: number;
    current_grade?: number;
    next_grade?: number;
    class_id?: number;
    is_user_manager?: boolean;
    receive_email_enabled?: boolean;
    photo?: IFile;
  }) => void;
  deleteTeacher?: (id: number) => void;
}

const validates: { [key: string]: (value: any) => string } = {};

validates["email"] = val => {
  return val.email.match(emailRegex)
    ? ""
    : "正しいメールアドレスを入力してください。";
};
validates["first_name"] = val => {
  return val.first_name ? "" : "先生の名を入力してください。";
};

validates["last_name"] = val => {
  return val.last_name ? "" : "先生の姓を入力してください。";
};

validates["first_name_kana"] = val => {
  return val.first_name_kana ? "" : "先生の名(カナ)を入力してください。";
};

validates["last_name_kana"] = val => {
  return val.last_name_kana ? "" : "先生の姓(カナ)を入力してください。";
};

validates["class_id"] = val => {
  return val.use_as_g4s_student
    ? val.class_id
      ? ""
      : "生徒のクラスを選択してください。"
    : "";
};

validates["next_grade"] = val => {
  return val.next_grade < val.current_grade
    ? "次の学年の指定に誤りがあります"
    : "";
};

export const TeacherForm = (props: TeacherFormProps) => {
  const ctx = React.useContext(ModalContext);
  const { currentYear } = useCurrentYear();
  const current_grade = sessionStorage.getItem("current_grade")
    ? parseInt(sessionStorage.getItem("current_grade") as string, 10)
    : 1;
  const next_grade = current_grade + 1;
  const entrance_year = currentYear - current_grade + 1;
  const [state, setState] = React.useState({
    id: props.teacher ? props.teacher.id : 0,
    first_name: props.teacher ? props.teacher.first_name : "",
    last_name: props.teacher ? props.teacher.last_name : "",
    first_name_kana: props.teacher ? props.teacher.first_name_kana : "",
    last_name_kana: props.teacher ? props.teacher.last_name_kana : "",
    email: props.teacher ? props.teacher.email : "",
    is_user_manager: props.teacher?.is_user_manager ?? false,
    receive_email_enabled: props.teacher?.receive_email_enabled ?? true,
    serviced: props.teacher ? props.teacher.serviced : true,
    use_as_g4s_student: props.teacher
      ? props.teacher.use_as_g4s_student
      : false,
    entrance_year:
      props.teacher &&
      props.teacher.use_as_g4s_student &&
      props.teacher.entrance_year
        ? props.teacher.entrance_year
        : entrance_year,
    current_grade:
      props.teacher &&
      props.teacher.use_as_g4s_student &&
      props.teacher.current_grade
        ? props.teacher.current_grade
        : current_grade,
    next_grade:
      props.teacher &&
      props.teacher.use_as_g4s_student &&
      props.teacher.next_grade
        ? props.teacher.next_grade
        : next_grade,
    class_id:
      props.teacher && props.teacher.use_as_g4s_student
        ? props.teacher.g4s_class_id
        : null,
    photo: props.teacher && props.teacher.photo ? props.teacher.photo : null,
    isSubmitted: false,
    errorMessages: {} as { [key: string]: string },
  });
  const photoInput = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setState(s => {
      if (props.teacher) {
        return {
          ...s,
          id: props.teacher.id,
          first_name: props.teacher.first_name,
          last_name: props.teacher.last_name,
          first_name_kana: props.teacher.first_name_kana,
          last_name_kana: props.teacher.last_name_kana,
          email: props.teacher.email,
          serviced: props.teacher.serviced,
          use_as_g4s_student: props.teacher.use_as_g4s_student,
          class_id:
            props.teacher && props.teacher.use_as_g4s_student
              ? props.teacher.g4s_class_id
              : null,
          entrance_year:
            props.teacher &&
            props.teacher.use_as_g4s_student &&
            props.teacher.entrance_year
              ? props.teacher.entrance_year
              : entrance_year,
          current_grade:
            props.teacher &&
            props.teacher.use_as_g4s_student &&
            props.teacher.current_grade
              ? props.teacher.current_grade
              : current_grade,
          next_grade:
            props.teacher &&
            props.teacher.use_as_g4s_student &&
            props.teacher.next_grade
              ? props.teacher.next_grade
              : next_grade,
          photo: props?.teacher?.photo || null,
        };
      } else {
        return { ...s, entrance_year };
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.teacher, entrance_year]);

  const onSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errorMessages = {} as { [key: string]: string };
      Object.keys(validates).forEach(key => {
        const ret = validates[key](state);
        if (ret) {
          errorMessages[key] = ret;
        }
      });
      if (Object.keys(errorMessages).length > 0) {
        setState(s => ({ ...s, errorMessages }));
        return;
      }
      ctx.actions.showModal(
        <Confirm
          calling={props.calling}
          closeModal={() => {
            ctx.actions.closeModal();
          }}
          content={
            props.teacher ? (
              props.teacher.has_account ||
              !state.serviced ||
              props.teacher.email === state.email ? (
                <p>先生を更新します、よろしいですか？</p>
              ) : (
                <>
                  <p className="mb-0">先生を更新します、よろしいですか？</p>
                  <p>※{state.email} に招待メールが送信されます</p>
                </>
              )
            ) : (
              <>
                <p className="mb-0">先生を登録します、よろしいですか？</p>
                <p>※{state.email} に招待メールが送信されます</p>
              </>
            )
          }
          callback={() => {
            if (state.use_as_g4s_student) {
              props.upsertTeacher({
                id: state.id,
                last_name: state.last_name,
                first_name: state.first_name,
                last_name_kana: state.last_name_kana,
                first_name_kana: state.first_name_kana,
                email: state.email,
                is_user_manager: state.is_user_manager,
                receive_email_enabled: state.receive_email_enabled,
                serviced: state.serviced,
                use_as_g4s_student: true,
                class_id: state.class_id!,
                entrance_year: state.entrance_year!,
                current_grade: state.current_grade!,
                next_grade: state.next_grade!,
                photo: state.photo!,
              });
            } else {
              props.upsertTeacher({
                id: state.id,
                last_name: state.last_name,
                first_name: state.first_name,
                last_name_kana: state.last_name_kana,
                first_name_kana: state.first_name_kana,
                email: state.email,
                is_user_manager: state.is_user_manager,
                receive_email_enabled: state.receive_email_enabled,
                serviced: state.serviced,
                use_as_g4s_student: true,
                photo: state.photo!,
              });
            }
            ctx.actions.closeModal();
          }}
        />,
      );
    },
    [state, props, ctx.actions],
  );

  const onChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const key = e.currentTarget.name;
      const val =
        e.currentTarget.type === "radio"
          ? e.currentTarget.value === "true"
          : e.currentTarget.type === "checkbox"
          ? e.currentTarget.checked
          : e.currentTarget.value;
      const args = { [key]: val };
      const ret = validates[key] ? validates[key](args) : "";
      const resetErrorCheckbox = {
        class_id:
          e.currentTarget.type === "checkbox" && !e.currentTarget.checked
            ? ""
            : state.errorMessages.class_id,
        next_grade:
          e.currentTarget.type === "checkbox" && !e.currentTarget.checked
            ? ""
            : state.errorMessages.next_grade,
      };
      setState(prevState => ({
        ...prevState,
        [key]: val,
        errorMessages: {
          ...prevState.errorMessages,
          [key]: ret,
          ...resetErrorCheckbox,
        },
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onChangeSelect = React.useCallback(
    (e: React.SyntheticEvent<HTMLSelectElement>) => {
      const key = e.currentTarget.name;
      const val = parseInt(e.currentTarget.value, 10);
      const errorMessages = {} as { [key: string]: string };

      setState(prevState => {
        let args = { ...prevState, [key]: val };

        if (key === "current_grade") {
          const classesGrade = props.classes?.filter(c => c.grade === val);
          args = {
            ...args,
            class_id: classesGrade?.some(c => c.id === state.class_id)
              ? state.class_id
              : 0,
            next_grade: val === 6 ? 6 : val + 1,
          };
        }

        Object.keys(validates).forEach(k => {
          errorMessages[k] = validates[k](args);
        });
        return {
          ...args,
          errorMessages,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.classes],
  );

  const onChangeClass = (class_id: number) => {
    const errorMessages = {} as { [key: string]: string };
    setState(prevState => {
      const args = { ...prevState, class_id };
      Object.keys(validates).forEach(k => {
        errorMessages[k] = validates[k](args);
      });
      return {
        ...args,
        errorMessages,
      };
    });
  };

  const onChangeServiced = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked;
    setState(prevState => ({
      ...prevState,
      is_user_manager: checked ? false : prevState.is_user_manager,
      receive_email_enabled: checked ? false : prevState.receive_email_enabled,
      serviced: !checked,
    }));
  };

  const onChangePhoto = (e: any) => {
    const file = e.currentTarget.files[0];
    if (!file) {
      return;
    }
    if (!file.type.match(/^image\//)) {
      alert("画像以外のファイルはアップロードできません");
      return;
    }
    if (
      file.type.toLowerCase() !== "image/jpg" &&
      file.type.toLowerCase() !== "image/jpeg" &&
      file.type.toLowerCase() !== "image/png"
    ) {
      alert("jpg/png以外の画像はアップロードできません");
      return;
    }
    const fileSizeByMB = file.size / 1024 / 1024;
    if (fileSizeByMB > MAX_IMAGE_SIZE_MB) {
      e.currentTarget.value = "";
      alert(
        `アップロードに失敗しました。\nファイルサイズを${MAX_IMAGE_SIZE_MB}MB以下にしてください。`,
      );
      return;
    }
    setState(prev => ({
      ...prev,
      photo: {
        ...(props.teacher?.photo as IFile),
        name: file.name,
        url: URL.createObjectURL(file),
        blob: file,
      },
    }));
  };

  const onDeletePhoto = () => {
    (photoInput.current as HTMLInputElement).value = "";
    setState({
      ...state,
      photo: {
        ...(state.photo as IFile),
        url: "",
        blob: undefined,
        deleted: true,
      },
    });
  };

  return (
    <div className="container">
      <div className={styles.pageTitle}>{props.title}</div>
      <div className={styles.inputAreaWrapper}>
        <form
          onSubmit={onSubmit}
          className={state.isSubmitted ? "was-validated" : ""}
        >
          {props.error && (
            <div className={styles.errorBillboard}>{props.error}</div>
          )}
          <div>
            <label
              className={clsx("form-label", styles.required)}
              htmlFor="last_name"
            >
              姓
            </label>
            <div className="input-group">
              <input
                type="text"
                id="last_name"
                name="last_name"
                className={
                  "form-control " +
                  (state.errorMessages["last_name"]
                    ? "is-invalid"
                    : state.last_name
                    ? "is-valid"
                    : "")
                }
                value={state.last_name}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["last_name"]}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label
              className={clsx("form-label", styles.required)}
              htmlFor="first_name"
            >
              名
            </label>
            <div className="input-group">
              <input
                type="text"
                id="first_name"
                name="first_name"
                className={
                  "form-control " +
                  (state.errorMessages["first_name"]
                    ? "is-invalid"
                    : state.first_name
                    ? "is-valid"
                    : "")
                }
                value={state.first_name}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["first_name"]}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label
              className={clsx("form-label", styles.required)}
              htmlFor="last_name_kana"
            >
              姓(カナ)
            </label>
            <div className="input-group">
              <input
                type="text"
                id="last_name_kana"
                name="last_name_kana"
                className={
                  "form-control " +
                  (state.errorMessages["last_name_kana"]
                    ? "is-invalid"
                    : state.last_name_kana
                    ? "is-valid"
                    : "")
                }
                value={state.last_name_kana}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["last_name_kana"]}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label
              className={clsx("form-label", styles.required)}
              htmlFor="first_name_kana"
            >
              名(カナ)
            </label>
            <div className="input-group">
              <input
                type="text"
                id="first_name_kana"
                name="first_name_kana"
                className={
                  "form-control " +
                  (state.errorMessages["first_name_kana"]
                    ? "is-invalid"
                    : state.first_name_kana
                    ? "is-valid"
                    : "")
                }
                value={state.first_name_kana}
                onChange={onChange}
              />
              <div className="invalid-feedback">
                {state.errorMessages["first_name_kana"]}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label
              className={clsx("form-label", styles.required)}
              htmlFor="email"
            >
              メールアドレス
            </label>
            <div className="input-group">
              <input
                type="email"
                name="email"
                autoComplete="off"
                className={
                  "form-control " +
                  (state.errorMessages["email"]
                    ? "is-invalid"
                    : state.email
                    ? "is-valid"
                    : "")
                }
                id="email"
                value={state.email}
                onChange={onChange}
              />
            </div>
            <div className="invalid-feedback">
              {state.errorMessages["email"]}
            </div>
          </div>
          <div className="mt-3">
            <label htmlFor="photo">顔写真</label>
            <div className="input-group">
              <input
                ref={photoInput}
                type="file"
                id="photo"
                accept=".png,.jpg,.jpeg"
                onChange={onChangePhoto}
              />
            </div>
            {state.photo?.url && (
              <div className="mt-3">
                <div className="row align-items-baseline justify-content-between">
                  <div className="col col-8">
                    <Avatar
                      src={state.photo.url}
                      alt="プロフィール画像"
                      size={96}
                    />
                  </div>
                  <div className="col col-4">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      disabled={!state.photo.url}
                      onClick={onDeletePhoto}
                    >
                      写真を削除
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={clsx("mt-4 mb-3", styles.checkboxWrapper)}>
            <div className={styles.checkboxForm}>
              <input
                type="checkbox"
                name="is_user_manager"
                id="is_user_manager"
                checked={state.is_user_manager}
                disabled={
                  !state.serviced ||
                  props.isMyself ||
                  props.teacher?.roles === 1
                }
                onChange={onChange}
              />
              <label htmlFor="is_user_manager">
                <strong>
                  ENAGEEDサービス管理画面へのアクセスを許可（推奨）
                </strong>
              </label>
            </div>
            <p className={styles.hint}>
              ※この項目にチェックを入れることで、こちらの「ENAGEEDサービス学校管理者画面」へのアクセスが可能になります。先生方ご自身で、生徒情報をはじめとした各種データの編集・削除が行えるようになるため、チェックを入れていただくことを推奨しています。
            </p>
          </div>
          <div className={clsx("mt-4 mb-3", styles.checkboxWrapper)}>
            <div className={styles.checkboxForm}>
              <input
                type="checkbox"
                name="receive_email_enabled"
                id="receive_email_enabled"
                checked={state.receive_email_enabled}
                disabled={!state.serviced}
                onChange={onChange}
              />
              <label htmlFor="receive_email_enabled">
                <strong>ENAGEEDサービスからの授業報告を許可する（推奨）</strong>
              </label>
            </div>
            <p className={styles.hint}>
              ※この項目にチェックを入れることで、担当授業ごとの回答サマリが自動で配信されます。授業の振り返りを簡易的に行えるようになりますので、チェックを入れていただくことを推奨しています。
            </p>
          </div>
          {props.teacher && (
            <div className={clsx("mt-4", styles.checkboxWrapper)}>
              <div className={styles.checkboxForm}>
                <input
                  type="checkbox"
                  name="serviced"
                  id="serviced"
                  checked={!state.serviced}
                  disabled={props.isMyself || props.teacher?.roles === 1}
                  onChange={onChangeServiced}
                />
                <label htmlFor="serviced">アカウントを無効にする</label>
              </div>
              <p className={styles.hint}>
                ※チェックを入れると、すべてのサービスにログインできなくなります
                <br />
                ※先生が異動や退職された場合にお使いください
              </p>
            </div>
          )}
          {props.teacher && !!props.school.g4s_enabled && (
            <>
              {state.use_as_g4s_student && (
                <div className={styles.gearForm}>
                  <h5 className={styles.subTitle}>生徒画面設定</h5>
                  <h6 className={clsx("mb-2", styles.description)}>
                    先生のアカウントで生徒画面にログインをすることができます
                    <br />
                    先生が生徒画面を利用する際の「クラス」を選択してください
                  </h6>
                  <div className="mb-4 d-flex justify-content-between">
                    <div className="w-25 d-flex flex-column align-items-center">
                      <label className="form-label" htmlFor="entrance_year">
                        導入年度
                      </label>
                      <select
                        name="entrance_year"
                        className="form-select"
                        aria-label="select"
                        value={state.entrance_year}
                        onChange={onChangeSelect}
                      >
                        {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map(m => {
                          return (
                            <option key={m} value={m + currentYear}>
                              {m + currentYear}年
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="w-25 d-flex flex-column align-items-center">
                      <label className="form-label" htmlFor="entrance_year">
                        現在の学年
                      </label>
                      <select
                        name="current_grade"
                        className="form-select"
                        aria-label="select"
                        value={state.current_grade}
                        onChange={onChangeSelect}
                      >
                        {[1, 2, 3, 4, 5, 6].map(m => {
                          return (
                            <option key={m} value={m}>
                              {m}年
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="w-25 d-flex flex-column align-items-center">
                      <label className="form-label" htmlFor="entrance_year">
                        次の学年
                      </label>
                      <select
                        name="next_grade"
                        className="form-select"
                        aria-label="select"
                        value={state.next_grade}
                        onChange={onChangeSelect}
                      >
                        {[1, 2, 3, 4, 5, 6].map(m => {
                          return (
                            <option key={m} value={m}>
                              {m}年
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  {state.errorMessages["next_grade"] && (
                    <p className={styles.error}>
                      {state.errorMessages["next_grade"]}
                    </p>
                  )}
                  <p className={styles.required}>クラス</p>
                  <div className={styles.listClass}>
                    <RadioGroup
                      name="class_id"
                      options={(props.classes ?? [])
                        .filter(
                          c =>
                            c.grade === state.current_grade ||
                            c.full_name === "先生アカウント専用クラス",
                        )
                        .reduce((acc: { [key: string]: string }, c) => {
                          acc[`${c.id}`] = c.full_name;
                          return acc;
                        }, {})}
                      value={state.class_id}
                      onChange={e => onChangeClass(parseInt(e.target.value))}
                    />
                  </div>
                  {state.errorMessages["class_id"] && (
                    <p className={styles.error}>
                      {state.errorMessages["class_id"]}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          <button
            className={"mt-4 btn btn-primary"}
            onClick={onSubmit}
            disabled={
              Object.keys(state.errorMessages).filter(
                k => state.errorMessages[k],
              ).length > 0 || props.calling
            }
          >
            {state.id === 0 ? "登録" : "更新"}
          </button>
        </form>
        {props.teacher && props.deleteTeacher && (
          <div className={styles.deleteArea}>
            {props.teacher.can_destroy ? (
              <p>まだ利用データがない場合、削除することができます</p>
            ) : (
              <p>利用データがあるため削除できません</p>
            )}
            <button
              className="btn btn-danger"
              onClick={() => {
                const { id, last_name, first_name } = props.teacher!;
                ctx.actions.showModal(
                  <Confirm
                    calling={props.calling}
                    closeModal={() => {
                      ctx.actions.closeModal();
                    }}
                    content={
                      <>
                        <p className="mb-0">
                          {last_name}
                          {first_name}を削除します。
                        </p>
                        <p>本当に削除してもよろしいですか？</p>
                      </>
                    }
                    variant="danger"
                    confirmText="削除"
                    callback={() => {
                      props.deleteTeacher!(id);
                      ctx.actions.closeModal();
                    }}
                  />,
                );
              }}
              disabled={!props.teacher.can_destroy || props.calling}
            >
              削除
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherForm;
